import React, { useEffect, useState, useRef } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Avatar,
  Switch,
  Input,
  Form,
  Select,
  Typography,
  Tag,
  Upload,
  List,
  Spin,
  Modal,
} from "antd";
import {
  UserOutlined,
  EditOutlined,
  SaveOutlined,
  PhoneOutlined,
  CommentOutlined,
  UploadOutlined,
  ArrowLeftOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  LockOutlined,
  UnlockOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  MessageOutlined,
  CameraOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";
import { TbCurrencyRupee } from "react-icons/tb";
import { useNavigate, useParams } from "react-router-dom";
// Removed unused imports
import { uploadImageToCloudinary } from "../../../utils/imageUpload";
import { ToastContainer, toast } from "react-toastify";
import { motion } from "framer-motion";
import {
  useGetConsultantByidQuery,
  useUpdateConsultantByAdminMutation,
  useGetDetailedReportsQuery,
} from "../../../redux/rtk/apiRtk";
import { consultantCategory } from "../../../components/consultantCategory";

const { Option } = Select;
const { Title, Text } = Typography;

function ConsultantProfileDetails() {
  const { id } = useParams();

  const { data, isLoading } = useGetConsultantByidQuery(id);
  const [updateConsulatnt] = useUpdateConsultantByAdminMutation();

  // Get detailed reports for this consultant
  const { data: reportsData, isLoading: reportsLoading } =
    useGetDetailedReportsQuery({
      type: "consultant",
      id: id,
    });

  // Theme color
  const primaryColor = "#A40165";

  // State
  const [formData, setFormData] = useState({
    phone: "",
    about: "",

    availability: [],
    pricePerMinute: "",
    communicationType: [],
    image: "",
    firstName: "",
    lastName: "",
    category: [],
  });
  const [activeConsultant, setActiveConsultant] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [animateProfile, setAnimateProfile] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);

  // Refs
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Redux

  // Router
  const navigate = useNavigate();

  // Form
  const [form] = Form.useForm();

  // Initial data fetch
  useEffect(() => {
    if (data) {
      setFormData(data);
      setActiveConsultant(data);
    }
  }, [id, data]);

  // Animation effect
  useEffect(() => {
    setTimeout(() => {
      setAnimateProfile(true);
    }, 100);
  }, []);

  // Handle form field changes
  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle image upload from file
  const handleImageChange = (file) => {
    setImage(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    return false; // Prevent auto upload
  };

  // Handle camera capture
  const handleCameraCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.style.display = "block";
        videoRef.current.play();

        // Hide upload options and show camera controls
        const uploadOptions = document.getElementById("upload-options");
        const controls = document.getElementById("camera-controls");

        if (uploadOptions) {
          uploadOptions.style.display = "none";
        }
        if (controls) {
          controls.style.display = "flex";
        }
      }
    } catch (error) {
      console.error("Error accessing camera:", error);
      toast.error("Unable to access camera. Please check permissions.");
    }
  };

  // Capture photo from camera
  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      const context = canvas.getContext("2d");

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          const file = new File([blob], "camera-photo.jpg", {
            type: "image/jpeg",
          });
          handleImageChange(file);
          stopCamera();
          setShowImageModal(false);
        },
        "image/jpeg",
        0.8
      );
    }
  };

  // Stop camera stream
  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach((track) => track.stop());
      videoRef.current.srcObject = null;
      videoRef.current.style.display = "none";

      // Show upload options and hide camera controls
      const uploadOptions = document.getElementById("upload-options");
      const controls = document.getElementById("camera-controls");

      if (uploadOptions) {
        uploadOptions.style.display = "block";
      }
      if (controls) {
        controls.style.display = "none";
      }
    }
  };

  // Handle file upload from computer
  const handleFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle file input change
  const handleFileInputChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleImageChange(file);
      setShowImageModal(false);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      // Validate form
      await form.validateFields();

      // Prepare data
      const updatedData = { ...formData };

      // Upload image if changed
      if (image) {
        const imageUrl = await uploadImageToCloudinary(image);
        updatedData.image = imageUrl;
      }

      // Submit to API
      const res = await updateConsulatnt({ id, body: updatedData });

      // Success
      toast.success("Consultant profile updated successfully");

      // Refresh data and reset state
      setIsEditing(false);
      setImage(null);
      setImagePreview(null);
    } catch (error) {
      console.error("Error updating consultant:", error);
      toast.error("Failed to update consultant profile");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  // Loading state
  if (isLoading || !activeConsultant) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div
            className="w-12 h-12 rounded-full border-4 border-t-transparent animate-spin mx-auto mb-4"
            style={{
              borderColor: `${primaryColor} transparent transparent transparent`,
            }}
          ></div>
          <p style={{ color: primaryColor }}>Loading consultant profile...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="p-4 sm:p-6 lg:p-8"
    >
      <ToastContainer />

      {/* Back button */}
      <div className="mb-6">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/consultant-profile")}
          className="flex items-center"
          style={{ color: primaryColor }}
        >
          Back to Consultants
        </Button>
      </div>

      {/* Profile Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{
          opacity: animateProfile ? 1 : 0,
          y: animateProfile ? 0 : 20,
        }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-2xl overflow-hidden mb-6"
        style={{ boxShadow: "0 4px 20px rgba(164, 1, 101, 0.07)" }}
      >
        {/* Header background with softer gradient */}
        <div
          className="h-28 relative overflow-hidden"
          style={{
            background: `linear-gradient(135deg, ${primaryColor}CC 0%, ${primaryColor}99 100%)`,
          }}
        >
          {/* Decorative elements with softer opacity */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full transform translate-x-1/3 -translate-y-1/2"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white opacity-5 rounded-full transform -translate-x-1/3 translate-y-1/2"></div>
          <div className="absolute bottom-0 right-1/4 w-32 h-32 bg-white opacity-5 rounded-full"></div>
        </div>

        {/* Profile info */}
        <div className="px-6 pb-6 relative">
          <div className="flex flex-col md:flex-row items-start md:items-center">
            {/* Avatar with softer styling */}
            <div className="relative -mt-14 mb-4 md:mb-0">
              <div
                className="absolute inset-0 rounded-full opacity-20"
                style={{
                  background: `${primaryColor}10`,
                  transform: "scale(1.15)",
                }}
              ></div>

              {isEditing ? (
                <div
                  className="relative cursor-pointer group"
                  onClick={() => setShowImageModal(true)}
                >
                  <Avatar
                    size={110}
                    src={imagePreview || activeConsultant.image}
                    icon={
                      !imagePreview &&
                      !activeConsultant.image && (
                        <UserOutlined style={{ color: "#888" }} />
                      )
                    }
                    className="border-3 border-white shadow-md transition-all duration-300 group-hover:shadow-xl"
                    style={{
                      boxShadow: `0 5px 15px rgba(164, 1, 101, 0.1)`,
                    }}
                  />
                  <div
                    className="absolute inset-0 flex items-center justify-center rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100"
                    style={{
                      backgroundColor: "rgba(0, 0, 0, 0.4)",
                      zIndex: 5,
                    }}
                  >
                    <UploadOutlined
                      style={{
                        fontSize: "28px",
                        color: "white",
                      }}
                    />
                  </div>
                </div>
              ) : (
                <Avatar
                  size={110}
                  src={imagePreview || activeConsultant.image}
                  icon={
                    !imagePreview &&
                    !activeConsultant.image && (
                      <UserOutlined style={{ color: "#888" }} />
                    )
                  }
                  className="border-3 border-white shadow-md"
                  style={{ boxShadow: `0 5px 15px rgba(164, 1, 101, 0.1)` }}
                />
              )}
            </div>

            {/* Name and status */}
            <div className="md:ml-6 flex-1">
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div>
                  <Title
                    level={3}
                    style={{ margin: 0, fontWeight: 400, color: "#333" }}
                  >
                    {activeConsultant.firstName} {activeConsultant.lastName}
                  </Title>

                  <div className="flex items-center mt-2">
                    {activeConsultant.isBlocked ? (
                      <Tag
                        color="error"
                        icon={<CloseCircleFilled />}
                        style={{
                          fontWeight: "normal",
                          opacity: 0.9,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          height: "24px",
                          lineHeight: "1",
                        }}
                      >
                        Blocked
                      </Tag>
                    ) : (
                      <Tag
                        color="success"
                        icon={<CheckCircleFilled />}
                        style={{
                          fontWeight: "normal",
                          opacity: 0.9,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          height: "24px",
                          lineHeight: "1",
                        }}
                      >
                        Active
                      </Tag>
                    )}

                    <Tag
                      className="ml-2"
                      style={{
                        backgroundColor: `${primaryColor}08`,
                        color: `${primaryColor}CC`,
                        border: `1px solid ${primaryColor}20`,
                        fontWeight: "normal",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        height: "24px",
                        lineHeight: "1",
                      }}
                    >
                      <TbCurrencyRupee style={{ fontSize: "14px" }} />
                      {activeConsultant.pricePerMinute}/min
                    </Tag>
                  </div>
                </div>

                {/* Edit/Save button */}
                <div className="mt-4 md:mt-0">
                  {isEditing ? (
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      loading={isSubmitting}
                      onClick={handleSubmit}
                      style={{
                        backgroundColor: primaryColor,
                        borderColor: primaryColor,
                      }}
                    >
                      Save Changes
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      onClick={() => setIsEditing(true)}
                      style={{
                        backgroundColor: primaryColor,
                        borderColor: primaryColor,
                      }}
                    >
                      Edit Profile
                    </Button>
                  )}
                </div>
              </div>

              {/* Categories */}
              {activeConsultant.category &&
                activeConsultant.category.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {activeConsultant.category.map((cat, index) => (
                      <Tag
                        key={index}
                        className="px-2 py-1 capitalize"
                        style={{
                          backgroundColor: `${primaryColor}15`,
                          color: primaryColor,
                          border: `1px solid ${primaryColor}30`,
                        }}
                      >
                        {cat}
                      </Tag>
                    ))}
                  </div>
                )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Reports Count Section */}
      {reportsData && reportsData.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{
            opacity: animateProfile ? 1 : 0,
            y: animateProfile ? 0 : 20,
          }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-6"
        >
          <Card
            className="rounded-xl shadow-md overflow-hidden"
            style={{ boxShadow: "0 4px 20px rgba(164, 1, 101, 0.07)" }}
          >
            <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-100 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100">
                    <ExclamationCircleOutlined className="text-red-600 text-xl" />
                  </div>
                  <div>
                    <Text strong className="text-red-800 text-lg block">
                      {
                        new Set(
                          reportsData.map((report) => report.submitter._id)
                        ).size
                      }{" "}
                      Unique Reporter
                      {new Set(
                        reportsData.map((report) => report.submitter._id)
                      ).size !== 1
                        ? "s"
                        : ""}
                    </Text>
                    <Text type="secondary" className="text-sm">
                      {reportsData.length} total report
                      {reportsData.length !== 1 ? "s" : ""} filed against this
                      consultant
                    </Text>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Tag color="red" className="font-medium px-3 py-1">
                    Requires Attention
                  </Tag>
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      )}

      {/* Profile Content */}
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          ...activeConsultant,
          pricePerMinute: activeConsultant.pricePerMinute
            ? activeConsultant.pricePerMinute.toString()
            : "0",
        }}
      >
        <Row gutter={[24, 24]}>
          {/* Left Column */}
          <Col xs={24} lg={8}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{
                opacity: animateProfile ? 1 : 0,
                y: animateProfile ? 0 : 20,
              }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              {/* Account Status */}
              <Card
                className="rounded-xl shadow-md mb-6 overflow-hidden"
                title={
                  <div className="flex items-center">
                    <div
                      className="flex items-center justify-center w-8 h-8 rounded-full mr-3"
                      style={{ background: `${primaryColor}15` }}
                    >
                      {activeConsultant.isBlocked ? (
                        <LockOutlined style={{ color: primaryColor }} />
                      ) : (
                        <UnlockOutlined style={{ color: primaryColor }} />
                      )}
                    </div>
                    <span>Account Status</span>
                  </div>
                }
                headStyle={{ borderBottom: `1px solid ${primaryColor}20` }}
                bodyStyle={{ padding: "20px" }}
              >
                <div className="flex items-center justify-between">
                  <Text>Block/Unblock Account</Text>
                  {isEditing ? (
                    <Switch
                      checked={formData.isBlocked}
                      className="bg-green-500"
                      onChange={(checked) => {
                        setFormData((prev) => ({
                          ...prev,
                          isBlocked: checked,
                        }));
                      }}
                      checkedChildren="Blocked"
                      unCheckedChildren="Active"
                    />
                  ) : (
                    <div
                      className={`px-3 py-1 rounded-full text-white text-sm ${
                        activeConsultant.isBlocked
                          ? "bg-red-500"
                          : "bg-green-500"
                      }`}
                    >
                      {activeConsultant.isBlocked ? "Blocked" : "Active"}
                    </div>
                  )}
                </div>
                <Text type="secondary" className="block mt-2 text-sm">
                  {activeConsultant.isBlocked
                    ? "This account is currently blocked. The consultant cannot log in or provide services."
                    : "This account is currently active. The consultant can log in and provide services."}
                </Text>
              </Card>

              {/* Communication Settings */}
              <Card
                className="rounded-xl shadow-md mb-6 overflow-hidden"
                title={
                  <div className="flex items-center">
                    <div
                      className="flex items-center justify-center w-8 h-8 rounded-full mr-3"
                      style={{ background: `${primaryColor}15` }}
                    >
                      <CommentOutlined style={{ color: primaryColor }} />
                    </div>
                    <span>Communication Settings</span>
                  </div>
                }
                headStyle={{ borderBottom: `1px solid ${primaryColor}20` }}
                bodyStyle={{ padding: "20px" }}
              >
                <Form.Item
                  name="communicationType"
                  label="Communication Types"
                  rules={[
                    {
                      required: true,
                      message: "Please select at least one communication type",
                    },
                  ]}
                >
                  {isEditing ? (
                    <Select
                      mode="multiple"
                      placeholder="Select communication types"
                      onChange={(value) =>
                        handleChange("communicationType", value)
                      }
                      disabled={!isEditing}
                      style={{ width: "100%" }}
                    >
                      <Option value="phone">Phone</Option>
                      <Option value="chat">Chat</Option>
                    </Select>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {activeConsultant.communicationType &&
                      activeConsultant.communicationType.length > 0 ? (
                        activeConsultant.communicationType.map(
                          (type, index) => (
                            <Tag
                              key={index}
                              className="px-3 py-1 capitalize"
                              style={{
                                backgroundColor: `${primaryColor}15`,
                                color: primaryColor,
                                border: `1px solid ${primaryColor}30`,
                              }}
                            >
                              {type}
                            </Tag>
                          )
                        )
                      ) : (
                        <Text type="secondary">
                          No communication types specified
                        </Text>
                      )}
                    </div>
                  )}
                </Form.Item>

                <Form.Item
                  name="pricePerMinute"
                  label="Price Per Minute (₹)"
                  rules={[
                    {
                      required: true,
                      message: "Please enter price per minute",
                    },
                    {
                      validator: (_, value) => {
                        const num = Number(value);
                        if (isNaN(num) || num < 0 || num > 250) {
                          return Promise.reject(
                            "Price must be between 0 and 250"
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  {isEditing ? (
                    <Input
                      type="number"
                      min={0}
                      max={250}
                      onChange={(e) =>
                        handleChange("pricePerMinute", e.target.value)
                      }
                      disabled={!isEditing}
                      addonBefore="₹"
                      suffix="per minute"
                    />
                  ) : (
                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                      <Text strong>₹{activeConsultant.pricePerMinute}</Text> per
                      minute
                    </div>
                  )}
                </Form.Item>
              </Card>
            </motion.div>
          </Col>

          {/* Right Column */}
          <Col xs={24} lg={16}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{
                opacity: animateProfile ? 1 : 0,
                y: animateProfile ? 0 : 20,
              }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card
                className="rounded-xl shadow-md overflow-hidden"
                title={
                  <div className="flex items-center">
                    <div
                      className="flex items-center justify-center w-8 h-8 rounded-full mr-3"
                      style={{ background: `${primaryColor}15` }}
                    >
                      <UserOutlined style={{ color: primaryColor }} />
                    </div>
                    <span>Personal Information</span>
                  </div>
                }
                headStyle={{ borderBottom: `1px solid ${primaryColor}20` }}
                bodyStyle={{ padding: "20px" }}
              >
                <Row gutter={[24, 24]}>
                  {/* First Name */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="firstName"
                      label="First Name"
                      rules={[
                        { required: true, message: "Please enter first name" },
                      ]}
                    >
                      {isEditing ? (
                        <Input
                          placeholder="Enter first name"
                          onChange={(e) =>
                            handleChange("firstName", e.target.value)
                          }
                          disabled={!isEditing}
                        />
                      ) : (
                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                          {activeConsultant.firstName}
                        </div>
                      )}
                    </Form.Item>
                  </Col>

                  {/* Last Name */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="lastName"
                      label="Last Name"
                      rules={[
                        { required: true, message: "Please enter last name" },
                      ]}
                    >
                      {isEditing ? (
                        <Input
                          placeholder="Enter last name"
                          onChange={(e) =>
                            handleChange("lastName", e.target.value)
                          }
                          disabled={!isEditing}
                        />
                      ) : (
                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                          {activeConsultant.lastName}
                        </div>
                      )}
                    </Form.Item>
                  </Col>

                  {/* Phone */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="phone"
                      label="Phone Number"
                      rules={[
                        {
                          required: true,
                          message: "Please enter phone number",
                        },
                        {
                          pattern: /^\d+$/,
                          message: "Please enter a valid phone number",
                        },
                      ]}
                    >
                      {isEditing ? (
                        <Input
                          placeholder="Enter phone number"
                          onChange={(e) =>
                            handleChange("phone", e.target.value)
                          }
                          disabled={!isEditing}
                          prefix={
                            <PhoneOutlined className="text-gray-400 mr-2" />
                          }
                        />
                      ) : (
                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 flex items-center">
                          <PhoneOutlined className="text-gray-400 mr-2" />
                          {activeConsultant.phone}
                        </div>
                      )}
                    </Form.Item>
                  </Col>

                  {/* Categories */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="category"
                      label="Categories"
                      rules={[
                        {
                          required: true,
                          message: "Please select at least one category",
                        },
                      ]}
                    >
                      {isEditing ? (
                        <Select
                          mode="multiple"
                          placeholder="Select categories"
                          onChange={(value) => handleChange("category", value)}
                          disabled={!isEditing}
                          style={{ width: "100%" }}
                        >
                          {consultantCategory?.map((item, index) => (
                            <Option key={index} value={item.value}>
                              <div className="flex items-center">
                                <span>{item.title}</span>
                              </div>
                            </Option>
                          ))}
                        </Select>
                      ) : (
                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                          <div className="flex flex-wrap gap-2">
                            {activeConsultant.category &&
                            activeConsultant.category.length > 0 ? (
                              activeConsultant.category.map((cat, index) => (
                                <Tag
                                  key={index}
                                  className="capitalize"
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    height: "24px",
                                    lineHeight: "1",
                                  }}
                                >
                                  {cat}
                                </Tag>
                              ))
                            ) : (
                              <Text type="secondary">
                                No categories specified
                              </Text>
                            )}
                          </div>
                        </div>
                      )}
                    </Form.Item>
                  </Col>

                  {/* About */}
                  <Col xs={24}>
                    <Form.Item name="about" label="About">
                      {isEditing ? (
                        <Input.TextArea
                          placeholder="Enter consultant description"
                          rows={4}
                          onChange={(e) =>
                            handleChange("about", e.target.value)
                          }
                          disabled={!isEditing}
                        />
                      ) : (
                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 min-h-[100px]">
                          {activeConsultant.about || "No description provided"}
                        </div>
                      )}
                    </Form.Item>
                  </Col>
                </Row>

                {/* Action buttons */}
                {isEditing && (
                  <div className="flex justify-end mt-4">
                    <Button
                      onClick={() => {
                        setIsEditing(false);
                        setImagePreview(null);
                        form.resetFields();
                      }}
                      className="mr-3"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      loading={isSubmitting}
                      onClick={handleSubmit}
                      style={{
                        backgroundColor: primaryColor,
                        borderColor: primaryColor,
                      }}
                    >
                      Save Changes
                    </Button>
                  </div>
                )}
              </Card>
            </motion.div>
          </Col>
        </Row>
      </Form>
      {/* Reports Section - Right after profile header */}
      {reportsData && reportsData.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{
            opacity: animateProfile ? 1 : 0,
            y: animateProfile ? 0 : 20,
          }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="my-6"
        >
          <Card
            className="rounded-xl shadow-md overflow-hidden"
            title={
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div
                    className="flex items-center justify-center w-8 h-8 rounded-full mr-3"
                    style={{ background: `${primaryColor}15` }}
                  >
                    <ExclamationCircleOutlined
                      style={{ color: primaryColor }}
                    />
                  </div>
                  <span>Reports Against This Consultant</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Text strong className="text-red-700">
                    {
                      new Set(reportsData.map((report) => report.submitter._id))
                        .size
                    }{" "}
                    Reporter
                    {new Set(reportsData.map((report) => report.submitter._id))
                      .size !== 1
                      ? "s"
                      : ""}
                    , {reportsData.length} Report
                    {reportsData.length !== 1 ? "s" : ""} Filed
                  </Text>
                </div>
              </div>
            }
            headStyle={{ borderBottom: `1px solid ${primaryColor}20` }}
            bodyStyle={{ padding: "20px" }}
          >
            {reportsLoading ? (
              <div className="flex justify-center items-center py-8">
                <Spin size="large" tip="Loading reports..." />
              </div>
            ) : (
              <List
                itemLayout="vertical"
                dataSource={reportsData}
                renderItem={(report, index) => (
                  <List.Item
                    key={report._id}
                    className="border-b border-gray-100 last:border-b-0 pb-4 mb-4 last:mb-0"
                  >
                    <div className="flex items-start space-x-4">
                      {/* Reporter Avatar */}
                      <Avatar
                        size={48}
                        src={
                          report.submitter?.profilePic?.secure_url ||
                          report.submitter?.profilePic?.url
                        }
                        icon={<UserOutlined />}
                        className="border-2 border-white shadow-sm flex-shrink-0"
                        style={{ boxShadow: `0 0 0 2px ${primaryColor}30` }}
                      />

                      {/* Report Content */}
                      <div className="flex-1 min-w-0">
                        {/* Reporter Info */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <Text strong className="text-gray-800">
                              {report.submitter?.firstName}{" "}
                              {report.submitter?.lastName}
                            </Text>
                            <Tag
                              size="small"
                              className="text-xs"
                              style={{
                                backgroundColor: "#ff4d4f15",
                                color: "#ff4d4f",
                                border: "1px solid #ff4d4f30",
                              }}
                            >
                              Report #{index + 1}
                            </Tag>
                          </div>

                          {/* Report Date */}
                          <div className="flex items-center text-gray-500 text-sm">
                            <CalendarOutlined className="mr-1" />
                            {new Date(report.createdAt).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </div>
                        </div>

                        {/* Report Issue */}
                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                          <div className="flex items-start space-x-2">
                            <MessageOutlined
                              className="text-gray-400 mt-1 flex-shrink-0"
                              style={{ fontSize: "14px" }}
                            />
                            <Text className="text-gray-700 leading-relaxed">
                              {report.issue}
                            </Text>
                          </div>
                        </div>

                        {/* Report Type */}
                        <div className="mt-2">
                          <Tag
                            className="capitalize"
                            style={{
                              backgroundColor: `${primaryColor}08`,
                              color: primaryColor,
                              border: `1px solid ${primaryColor}15`,
                            }}
                          >
                            {report.type} Report
                          </Tag>
                        </div>
                      </div>
                    </div>
                  </List.Item>
                )}
              />
            )}
          </Card>
        </motion.div>
      )}

      {/* Image Upload Modal */}
      <Modal
        title={
          <div className="flex items-center gap-3 pb-2">
            <div
              className="flex items-center justify-center w-12 h-12 rounded-xl shadow-sm"
              style={{
                background: `linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}25 100%)`,
                border: `1px solid ${primaryColor}20`,
              }}
            >
              <UploadOutlined
                style={{ color: primaryColor, fontSize: "20px" }}
              />
            </div>
            <div>
              <span className="text-xl font-semibold text-gray-800">
                Update Profile Photo
              </span>
              <div className="text-sm text-gray-500 mt-1">
                Choose your preferred method
              </div>
            </div>
          </div>
        }
        open={showImageModal}
        onCancel={() => {
          setShowImageModal(false);
          stopCamera();
        }}
        footer={null}
        centered
        width={600}
        className="image-upload-modal"
        styles={{
          header: {
            borderBottom: `2px solid ${primaryColor}15`,
            paddingBottom: "20px",
            marginBottom: "24px",
          },
        }}
      >
        <div className="py-2">
          {/* Camera Preview Section */}
          <div className="mb-6" id="camera-preview-container">
            <video
              ref={videoRef}
              className="w-full rounded-xl shadow-lg border-2 border-gray-200"
              style={{
                maxHeight: "350px",
                display: "none",
                objectFit: "cover",
              }}
              autoPlay
              playsInline
            />

            <div
              className="mt-4 flex justify-center gap-4"
              id="camera-controls"
              style={{ display: "none" }}
            >
              <button
                onClick={capturePhoto}
                className="flex items-center gap-3 px-6 py-3 rounded-xl font-semibold text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                style={{
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}DD 100%)`,
                }}
              >
                <CameraOutlined className="text-lg" />
                Capture Photo
              </button>
              <button
                onClick={stopCamera}
                className="flex items-center gap-3 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300"
              >
                Cancel
              </button>
            </div>
          </div>

          {/* Options Section */}
          <div id="upload-options">
            <p className="text-gray-600 mb-6 text-center text-lg">
              How would you like to update the profile photo?
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Camera Option */}
              <div
                onClick={handleCameraCapture}
                className="group cursor-pointer p-6 rounded-2xl border-2 border-gray-200 hover:border-blue-400 bg-gradient-to-br from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
              >
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300">
                    <CameraOutlined className="text-2xl text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">
                      Take Photo
                    </h3>
                    <p className="text-sm text-gray-600">
                      Use your camera to capture a new photo
                    </p>
                  </div>
                  <div className="w-full h-1 bg-blue-300 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </div>

              {/* Upload Option */}
              <div
                onClick={handleFileUpload}
                className="group cursor-pointer p-6 rounded-2xl border-2 border-gray-200 hover:border-green-400 bg-gradient-to-br from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
              >
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300">
                    <FolderOpenOutlined className="text-2xl text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">
                      Upload Photo
                    </h3>
                    <p className="text-sm text-gray-600">
                      Choose an existing photo from your device
                    </p>
                  </div>
                  <div className="w-full h-1 bg-green-300 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </div>
            </div>

            {/* Tips Section */}
            <div className="mt-8 p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg
                    className="w-4 h-4 text-yellow-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 text-sm mb-1">
                    Photo Guidelines
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Use a clear, professional photo where the consultant's face
                    is clearly visible. This helps build trust with clients.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        style={{ display: "none" }}
      />

      {/* Hidden canvas for camera capture */}
      <canvas ref={canvasRef} style={{ display: "none" }} />

      {/* Custom styles */}
      <style jsx="true">{`
        .ant-form-item-label > label {
          color: #666;
          font-weight: 500;
        }

        .ant-switch-checked {
          background-color: ${primaryColor} !important;
        }

        .ant-select-focused:not(.ant-select-disabled).ant-select:not(
            .ant-select-customize-input
          )
          .ant-select-selector {
          border-color: ${primaryColor} !important;
          box-shadow: 0 0 0 2px ${primaryColor}20 !important;
        }

        .ant-input:focus,
        .ant-input-focused {
          border-color: ${primaryColor} !important;
          box-shadow: 0 0 0 2px ${primaryColor}20 !important;
        }

        .ant-input:hover {
          border-color: ${primaryColor}80 !important;
        }

        .ant-list-item:hover {
          background-color: ${primaryColor}02 !important;
          border-radius: 8px;
          transition: all 0.3s ease;
        }

        .ant-empty-img-simple-ellipse {
          fill: ${primaryColor}20 !important;
        }

        .ant-empty-img-simple-path {
          fill: ${primaryColor}40 !important;
        }
      `}</style>
    </motion.div>
  );
}

export default ConsultantProfileDetails;
